import React, { Dispatch, SetStateAction, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { Link } from "react-router-dom";
import DeletePopUp from "../DeletePopUp";
import { IComplaint } from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { fetchComplaints } from "../../api/ComplaintApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { Eye, Pencil } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import { usePermission } from "../../hooks/usePermission";
import { darkenColor } from "../../exports/functions";

const getStatusClass = (status: string) => {
  switch (status) {
    case "Urgent":
      return "pill-danger";
    case "Not Urgent":
      return "pill-primary";
    case "Regular":
      return "pill-info";
    default:
      return "bg-secondary";
  }
};

const ComplaintTable = ({
  viewAll,
  complaint,
  setComplaint,
}: {
  viewAll?: boolean;
  complaint: IComplaint[];
  setComplaint: Dispatch<SetStateAction<IComplaint[]>>;
}) => {
  const { setLoading } = useLoader();
  const { statusOptions } = useOption();
  const { canEdit, canDelete } = usePermission();
  const isMobile = useMobile();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const itemsPerPage = showAll
    ? Math.max(complaint.length, 1)
    : viewAll
      ? 25
      : 5;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = complaint.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(complaint.length / itemsPerPage);
  const handleClose = () => {
    setShowDelete(false);
  };

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Complaint Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/complaints/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        fetchComplaints()
          .then((complaintData) => {
            setComplaint(complaintData);
            setShowDelete(false);
            setDeleteId(null);
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Complaint deleted successfully!");
            }, 1000);
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || "Something went wrong!";
            dynamicFailed(message);
            setTimeout(() => {
              setLoading(false);
            }, 1000);
          });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Something went wrong!";
      dynamicFailed(message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };
  return (
    <>
      <div className="table-dashboard-card">
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>S. no.</th>
                <th>Complaint Title</th>
                <th>Cooperative</th>
                <th>Category</th>
                <th>Filed on</th>
                <th>Priority</th>
                <th>Status</th>
                <th className="sticky-action-header">Actions</th>
              </tr>
            </thead>

            <tbody>
              {currentData.map((item, index) => {
                const canEditComplaint =
                  canEdit("complaints") &&
                  item.status?.toLowerCase() !== "solved";

                return (
                  <tr key={index}>
                    <td className="text-danger">
                      {" "}
                      {String(indexOfFirstItem + index + 1).padStart(6, "0")}
                    </td>
                    <td className="table-bold">{item.complainTitle}</td>
                    <td className="table-bold">{item.cooperativeName}</td>
                    <td>{item.complaintType}</td>
                    <td>{item.filedOn}</td>
                    <td>
                      {" "}
                      <span
                        className={`badge rounded-pill px-3 py-2 ${getStatusClass(
                          item.complaintPriority,
                        )}`}
                      >
                        {item.complaintPriority}
                      </span>
                    </td>

                    <td>
                      <span
                        className={`badge rounded-pill px-3 py-2`}
                        style={statusStyle(item.status || "")}
                      >
                        {item.status}
                      </span>
                    </td>

                    <td className="sticky-action">
                      <div className="d-flex gap-3 align-items-center">
                        <Link to={`/complaints/view/${item.id}`}>
                          <i
                            className="cursor-pointer"
                            data-toggle="tooltip"
                            data-placement="top"
                            title="View"
                          >
                            <Eye />
                          </i>
                        </Link>
                        <Link
                          to={
                            canEditComplaint
                              ? `/complaints/edit/${item.id}`
                              : "#"
                          }
                          onClick={(e) => {
                            if (!canEditComplaint) {
                              e.preventDefault();
                            }
                          }}
                          style={{
                            pointerEvents: canEditComplaint ? "auto" : "none",
                            opacity: canEditComplaint ? 1 : 0.5,
                          }}
                        >
                          <i
                            data-toggle="tooltip"
                            data-placement="top"
                            title="Edit"
                          >
                            <Pencil size={20} />
                          </i>
                        </Link>
                        <i
                          className={`fa-solid fa-trash text-danger ${
                            canDelete("complaints")
                              ? "cursor-pointer"
                              : "opacity-50"
                          }`}
                          data-toggle="tooltip"
                          data-placement="top"
                          title={
                            canDelete("complaints") ? "Delete" : "No permission"
                          }
                          style={{
                            pointerEvents: canDelete("complaints")
                              ? "auto"
                              : "none",
                          }}
                          onClick={() => {
                            if (!canDelete("complaints")) return;

                            setDeleteId(item?.id || 0);
                            setShowDelete(true);
                          }}
                        ></i>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          className={`d-flex justify-content-between align-items-center flex-wrap ${isMobile ? "gap-2" : ""} table-footer`}
        >
          <div className="d-flex align-items-center gap-3">
            <small>
              {showAll
                ? `All ${complaint.length} Complaints`
                : `${indexOfFirstItem + 1}–${Math.min(indexOfLastItem, complaint.length)} of ${complaint.length} Complaints`}
            </small>
            {complaint.length > (viewAll ? 25 : 5) && (
              <h6
                className="mb-0 cursor-pointer"
                onClick={() => {
                  setShowAll((prev) => !prev);
                  setCurrentPage(1);
                }}
              >
                {showAll
                  ? `Show ${viewAll ? 25 : 5} out of ${complaint.length}`
                  : "Show All"}
              </h6>
            )}
          </div>

          {!showAll && (
            <div className="pagination">
              <button
                className="pagination-arrow"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                <i className="fa-solid fa-angle-left"></i>
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  className={`pagination-page ${
                    currentPage === i + 1 ? "active" : ""
                  }`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="pagination-arrow"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                <i className="fa-solid fa-angle-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default ComplaintTable;
